import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import './FormComponent.scss';
import Schedule from './Schedule';
import Notification from './Notification';
import { ApplicationState } from '../../../store';
import { EmployeeScheduleRequest, ScheduleFormData, ShiftSchedule } from '../../../shared/models';
import { getEmpDetails, updateEmployeeSchedule } from '../../../store/empDetails/actions';
import DateHelper from '../../../helpers/DateHelper';
import { evaluateScheduleRestriction } from '../../../utils/evaluateScheduleRestriction';

// Simple quality validation configuration for notes when commentLengthCheck > 0
// These tokens are considered too generic or non-informative and will be rejected.
const DISALLOWED_NOTE_TOKENS = [
  'na','n/a','n.a','none','nil','xxx','xxxx','test','asdf','abc','blank',"123","1234", "12345"
];

/**
 * Evaluate whether a note is low quality. Returns an error message if invalid, else null.
 * Rules (only enforced when min length feature is active):
 *  - Exact match (case-insensitive) against disallowed tokens.
 *  - Alphanumeric-only version matches a disallowed token (e.g., "N/A" -> "na").
 *  - Composed of a single repeated character (e.g., "AAAA", "11111").
 */
const evaluateNoteQuality = (raw: string): string | null => {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null; // Length check handled separately
  const lower = trimmed.toLowerCase();
  const alnum = lower.replace(/[^a-z0-9]/g,'');
  if (DISALLOWED_NOTE_TOKENS.includes(lower) || DISALLOWED_NOTE_TOKENS.includes(alnum)) {
    return 'Please provide a meaningful reason – entries like "NA" / "XXXX" / "Test" are not allowed.';
  }
  if (alnum.length > 1 && new Set(alnum.split('')).size === 1) {
    return 'Please avoid repeating a single character; provide a brief meaningful description.';
  }
  return null;
};

// PTO Configuration Object
const PTO_OPTIONS = {
  DEFAULT: { value: '', display: 'Select your option', dbValue: null },
  NO: { value: 'No', display: 'No', dbValue: 1 },
  YES_HEALTH: { value: 'Yes_Health_Related', display: 'Yes – Health Related', dbValue: 2 },
  YES_NON_HEALTH: { value: 'Yes_Not_Health_Related', display: 'Yes – Not Health Related', dbValue: 3 }
};

const initialFormData: ScheduleFormData = {
  name: '',
  employeeNumber: '',
  email: '',
  location: '',
  contract: '',
  shiftSchedule: [],
  goingTobe: []
};

const employeeScheduleRequest: EmployeeScheduleRequest = {
  reasonDescription: '',
  absentReasonId: '',
  email: '',
  employeeId: '',
  scheduleId: '',
  formSubmitedLocalDateTime: '',
  formUpdateLocalDateTime: '',
  scheduleStartTimeDate: '',
  scheduleStartTime: '',
  scheduleStartTimeString: '',
  scheduleEndTimeDate: '',
  scheduleEndTime: '',
  scheduleEndTimeString: '',
  scheduleHours: '',
  note: '',
  adminNote: '',
  IsScheduleCorrect:true,
  IsJuryDuty:false,
  IsBereavement:false,
  IsApplyPTO: null // Default null for dropdown
};

const FormComponent: React.FC = () => {
  const dispatch = useDispatch();
  const { employeeDetail: employeeDetails, schedules: empScheduleDetails, absentDetails } = useSelector((state: ApplicationState) => state.empData);
  const loggedUserDetails = useSelector((state: ApplicationState) => state.internalUser);
  const [selectedScheduleId, setSelectedScheduleId] = useState<string | null>(null);

  const [isSubmited, setIsSubmited] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState<ScheduleFormData>(initialFormData);
  const [postData, setPostData] = useState(employeeScheduleRequest);
  const [isNotificationVisible, setIsNotificationVisible] = useState(false);
  const [isApiCalled, setIsApiCalled] = useState(false);
  const [isError, setIsError] = useState(true);
  const [notificationText, setNotificationText] = useState("Notification Sent");
  const [notificationColor, setNotificationColor] = useState("#333");
  const [notificationBgColor] = useState("#e6ffe6");

  useEffect(() => {
    const fetchEmployeeData = async () => {
      try {
        setLoading(true);

        if (!employeeDetails?.employeeId && loggedUserDetails?.idToken?.employeeid && loggedUserDetails?.idToken?.useremail && isApiCalled === false) {
           dispatch(getEmpDetails(loggedUserDetails.idToken?.employeeid, loggedUserDetails.idToken?.useremail) as any).then(_ => { setLoading(false); setIsApiCalled(true) });
         }
        console.log(employeeDetails.employeeId);
        const updatedFormData: ScheduleFormData = {
          name: `${employeeDetails.employeeFirstName} ${employeeDetails.employeeLastName}`,
          employeeNumber: employeeDetails.employeeId,
          email: employeeDetails.userPrincipalName,
          location: employeeDetails.stationName,
          contract: employeeDetails.contractName,
          shiftSchedule: empScheduleDetails as ShiftSchedule[],
          goingTobe: absentDetails
        };

        setFormData(updatedFormData);

        setPostData((prevData) => ({
          ...prevData,
          email: employeeDetails?.userPrincipalName,
          employeeId: employeeDetails?.employeeId,
        }));

      } catch (error) {
        console.error('Error fetching employee data:', error);
      } finally {
        if (employeeDetails.employeeId !== '') {
          setLoading(false);
        }
      }
    };

    fetchEmployeeData();
  }, [absentDetails, dispatch, empScheduleDetails, employeeDetails, employeeDetails.employeeId, loggedUserDetails.employeeid,
    loggedUserDetails.idToken?.employeeid, loggedUserDetails.idToken?.useremail, loggedUserDetails.useremail]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    if (e.target instanceof HTMLSelectElement) {
      if (name === "absentReasonId") {
        const selectedText = e.target.options[e.target.selectedIndex].text;
        setPostData((prevData) => ({
          ...prevData,
          absentReasonId: value,
          reasonDescription: selectedText,
        }));
      } else if (name === "IsApplyPTO") {
        // Find the option configuration that matches the selected value
        const selectedOption = Object.values(PTO_OPTIONS).find(option => option.value === value);
        const dbValue = selectedOption ? selectedOption.dbValue : null;
        
        setPostData((prevData) => ({
          ...prevData,
          IsApplyPTO: dbValue,
        }));
      }
    }
  };

  const handleNoteChange = (event: React.ChangeEvent<HTMLTextAreaElement>, field: 'note' | 'adminNote') => {
    const { value } = event.target;
    setPostData((prevData) => ({ ...prevData, [field]: value }));
  };

  
  

  const handleScheduleSelection = (selectedSchedule: ShiftSchedule) => {
    fillScheduleFields(selectedSchedule);
    setSelectedScheduleId(selectedSchedule.scheduleId); // Track selected schedule
  };

  const fillScheduleFields = (selectedSchedule: ShiftSchedule) => {
    const { stringSchedule_StartTime, stringSchedule_EndTime, scheduleId } = selectedSchedule;

    // Use the helper function to get the schedule fields
    const {
      scheduleStartTimeDate,
      scheduleStartTimeString,
      scheduleEndTimeDate,
      scheduleEndTimeString,
      scheduleHours,
    } = DateHelper.extractScheduleFields(stringSchedule_StartTime, stringSchedule_EndTime);

  // Update postData with the extracted values
    setPostData((prevData: EmployeeScheduleRequest) => ({
      ...prevData,
      scheduleId: scheduleId,
      scheduleStartTimeDate,
      scheduleStartTime: stringSchedule_StartTime,
      scheduleStartTimeString,
      scheduleEndTimeDate,
      scheduleEndTime: stringSchedule_EndTime,
      scheduleEndTimeString,
      scheduleHours: scheduleHours,
      formSubmitedLocalDateTime: new Date().toISOString(),
      formUpdateLocalDateTime: new Date().toISOString(),
    }));
  };

  // Helper function to set notification details
  const setNotification = (text: string, color: string, isError:boolean = true) => {
    setNotificationText(text);
    setNotificationColor(color);
    setIsNotificationVisible(true);
    setIsError(isError);
    setIsSubmited(false);
  };

  // Helper function to get the current selected PTO value for the dropdown
  const getCurrentPTOValue = () => {
    const currentOption = Object.values(PTO_OPTIONS).find(option => option.dbValue === postData.IsApplyPTO);
    return currentOption ? currentOption.value : PTO_OPTIONS.DEFAULT.value;
  };

  /**
   * Determine if the current submission is blocked by the schedule time restriction window.
   * Returns true if blocked (and sets a notification), false if allowed.
   */
  const checkScheduleRestriction = (): boolean => {
    if (!postData.scheduleId) return false;
    const selected = formData.shiftSchedule.find(s => s.scheduleId === postData.scheduleId);
    const result = evaluateScheduleRestriction(selected as any);
    if (result.blocked) {
      setNotification(result.message || 'Submission blocked by schedule restriction.', 'red', true);
      return true;
    }
    return false;
  };

  // Update validatePostData to invoke restriction logic first:
  const validatePostData = () => {

    // NEW: Check schedule restriction first
    if (checkScheduleRestriction()) {
      return false;
    }

    // Existing validations
    if (postData.scheduleId === '' && !employeeDetails.allowNoScheduleCallout) {
      setNotification("Required: Please select a Schedule.", "red");
      return false;
    }

    if (postData.absentReasonId === '') {
      setNotification("Required: Please select your answer for: Are you going to be?", "red");
      return false;
    }

    if (employeeDetails.isActiveQuestionApplyPTO === "True" && postData.IsApplyPTO === null) {
      setNotification("Required: Please select an option for applying PTO or time balance.", "red");
      return false;
    }

    const minLen = employeeDetails.commentLengthCheck || 0;
    const trimmedNote = postData.note.trim();
    if (minLen > 0) {
      // Minimum length enforcement
      if (trimmedNote.length < minLen) {
        setNotification(
          trimmedNote.length === 0
            ? `Required: A note of at least ${minLen} character(s) is required.`
            : `Please enter at least ${minLen} character(s) in the note (current: ${trimmedNote.length}).`,
          'red'
        );
        return false;
      }
      // Quality enforcement (only after length passes)
      const qualityError = evaluateNoteQuality(trimmedNote);
      if (qualityError) {
        setNotification(qualityError, 'red');
        return false;
      }
    } else {
      if (!postData.IsScheduleCorrect && trimmedNote === '') {
        setNotification('Required: Note to Station is mandatory if the schedule is incorrect.', 'red');
        return false;
      }
    }

    return true;
  };

  // Usage of the validatePostData function before performing the desired action
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsNotificationVisible(false);
    setIsSubmited(true);
    if (validatePostData()) {
      // Proceed with submission or further actions
      dispatch(updateEmployeeSchedule(postData) as any).then(response => {
        if (response === 'Success') {
          if(postData.scheduleStartTime !== ''){  
          setNotification(postData.reasonDescription+" notification for "+ DateHelper.scheduleDateFormat(new Date(postData.scheduleStartTime))+" was sent successfully.", "#07752f", false);
            //The absence notification for Monday, September 23rd at 5:00 AM was sent successfully  
        }
          else 
          {
            setNotification(postData.reasonDescription+" notification was sent successfully.", "#07752f", false);
          }
          setPostData((prevData) => ({
            ...prevData,
            note: '',
            adminNote: '',
            absentReasonId: '',
            IsScheduleCorrect:true,
            IsJuryDuty:false,
            IsBereavement:false,
            IsApplyPTO: null
          }));
         // Reset schedule to the first option after submission
         if (formData.shiftSchedule.length > 0) {
          fillScheduleFields(formData.shiftSchedule[0]);
          setSelectedScheduleId(formData.shiftSchedule[0].scheduleId); // Set the first schedule as selected
        }
        }
      })
    }
  };

  const handleNotificationClose = () => {
    setIsNotificationVisible(false);
  };

  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  const { name, value } = e.target;
  setPostData((prevData) => {
    if (
      name === "IsScheduleCorrect" ||
      name === "IsJuryDuty" ||
      name === "IsBereavement"
    ) {
      return { ...prevData, [name]: value === "true" };
    }
    return { ...prevData, [name]: value };
  });
};

  return (    
    <div>
       {(formData.name === "null null" || employeeDetails.employeeId === "") && loading === false ? ( 
          <div className="justify-content-center align-items-center mt-5">
            <div className="display-8 text-center">Oops! Something went wrong. We're unable to retrieve the data at the moment.</div>
            <div className="display-8 text-center">Please try again later or contact your manager to report this absence or late event.</div>
          </div>
        ):(
          
        <div>

      {loading ? (
        <div className="d-flex justify-content-center align-items-center"><div className="display-8 animate-text">Loading employee data...</div></div>
      ) : (
          <form className="form-container" onSubmit={handleSubmit}>
            {isNotificationVisible && (
              <Notification
                message={notificationText}
                backgroundColor={notificationBgColor}
                textColor={notificationColor}
                onClose={handleNotificationClose}
                isError={isError}
              />
            )}

           

            <div className="userinfo">
              <div className="uname">Welcome {formData.name} ({formData.employeeNumber})</div>
              
              {formData.shiftSchedule.length > 0 || employeeDetails.allowNoScheduleCallout ?(
              <div className="contactInfo">
                <div><strong>Location:</strong> {formData.location || "No location provided"}</div>
                <div><strong>Contract:</strong> {formData.contract}</div>
              </div>
              ):(
                <div className="text-danger">
                  <strong>Error:</strong> You do not have a schedule available. Please contact your manager to report this absence or late event.
                </div>
              )}
            </div>
            
            {(formData.shiftSchedule.length > 0 || employeeDetails.allowNoScheduleCallout) && (
                <div>
                 {(!employeeDetails.allowNoScheduleCallout || formData.shiftSchedule.length > 0) &&(<div className="form-group font-weight-light">
                    <label htmlFor="shiftSchedule">* Select a schedule (Please note that the schedule reflected is in the Kronos payroll system):</label>
                    <Schedule
                      schedules={formData.shiftSchedule}
                      onScheduleSelect={handleScheduleSelection}
                      selectedScheduleId={selectedScheduleId}
                    />
                  </div>
                )}

                  {/* radio for IsScheduleCorrect yes should be selected by default  */}
                  {(!employeeDetails.allowNoScheduleCallout || formData.shiftSchedule.length > 0) &&(<div className="form-group">
                    <label>Is the selected schedule listed above, correct? If not, please add your shift details in the 'Note to Station' section. Please note that Unifi will still adhere to our attendance policy, and the 'Notes' section is only for providing additional information.</label>
                    <div>
                      <label className='ml-2'>
                        <input className="mr-1" type="radio" name="IsScheduleCorrect" value="true" checked={postData.IsScheduleCorrect} onChange={handleRadioChange} />
                        Yes
                      </label>
                      <label className='ml-2'>
                        <input className="mr-1" type="radio" name="IsScheduleCorrect" value="false" checked={!postData.IsScheduleCorrect} onChange={handleRadioChange} />
                        No
                      </label>
                    </div>
                  </div>)}
                  
                  <div className="form-group">
                    <label htmlFor="goingTobe">* Please select below if you will be late or Absent:</label>
                    <div className='custom-dropdown'>
                      <select name="absentReasonId" value={postData.absentReasonId} onChange={handleInputChange}>
                        <option value="">Select your option</option>
                        {formData.goingTobe.map((schedule, index) => (
                          <option key={index} value={schedule.absentReasonId}>{schedule.reasonDescription}</option>
                        ))}
                      </select>
                      <div className="arrow-container">
                        <div className="arrow"></div>
                      </div>
                    </div>
                  </div>

                      


                  <div className="form-group">
                    <label>
                      {(!postData.IsScheduleCorrect && postData.note.trim() === '')?"* ":""}What is the reason for your late or absence, any notes for this event? - Please provide any additional information that needs to be shared with your shift leader. Do not include personal medical details. If there are any special circumstances or updates regarding your employment status, please make sure to mention them here :
                    </label>
                    <textarea
                      rows={3}
                      cols={50}
                      name="noteToManager"
                      maxLength={500}
                      value={postData.note}
                      onChange={(e) => handleNoteChange(e, 'note')} />
                    {employeeDetails.commentLengthCheck > 0 && (
                      <div className="text-muted small">
                        <div>
                          Minimum required characters: {employeeDetails.commentLengthCheck}. Current: {postData.note.trim().length}.
                        </div>
                        <div>
                          Provide a concise, meaningful reason (e.g., "Car trouble – will arrive 30 mins late"). Avoid entries like NA, XXXX, Test, or repeating a single character.
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="form-group hidden">
                    <label> Note to Admin - (Any additional action required? Please, No Personal Medical Details.):</label>
                    <textarea disabled
                      rows={2}
                      cols={50}
                      name="noteToAdmin"
                      maxLength={500}
                      value={postData.adminNote}
                      onChange={(e) => handleNoteChange(e, 'adminNote')} />
                  </div>

                   {employeeDetails.isActiveQuestionApplyPTO === "True" && (
                      <div className="form-group">
                        <label>
                          * Where available, would you like to apply any available PTO or other time balance to this absence?
                        </label>
                        <div className='custom-dropdown'>
                          <select name="IsApplyPTO" value={getCurrentPTOValue()} onChange={handleInputChange}>
                            <option value={PTO_OPTIONS.DEFAULT.value}>{PTO_OPTIONS.DEFAULT.display}</option>
                            <option value={PTO_OPTIONS.NO.value}>{PTO_OPTIONS.NO.display}</option>
                            <option value={PTO_OPTIONS.YES_HEALTH.value}>{PTO_OPTIONS.YES_HEALTH.display}</option>
                            <option value={PTO_OPTIONS.YES_NON_HEALTH.value}>{PTO_OPTIONS.YES_NON_HEALTH.display}</option>
                          </select>
                          <div className="arrow-container">
                            <div className="arrow"></div>
                          </div>
                        </div>
                      </div>
                    )}     

                   {/* radio for IsJuryDuty No should be selected by default  */}
                   {employeeDetails.isActiveQuestionJuryDuty && ( <div className="form-group">
                    <label>Is this callout related to jury duty? <span className='font-italic'>¹</span></label>
                    <div>
                      <label className='ml-2'>
                        <input className="mr-1" type="radio" name="IsJuryDuty" value="true" checked={postData.IsJuryDuty} onChange={handleRadioChange} />
                        Yes
                      </label>
                      <label className='ml-2'>
                        <input className="mr-1" type="radio" name="IsJuryDuty" value="false" checked={!postData.IsJuryDuty} onChange={handleRadioChange} />
                        No
                      </label>
                    </div>
                  </div>)}
                  {/* radio for IsBereavement yes should be selected by default  */}
                   {employeeDetails.isActiveQuestionBereavement && (<div className="form-group">
                    <label>Is this callout Related to bereavement? <span className='font-italic'>¹</span></label>
                    <div>
                      <label className='ml-2'>
                        <input className="mr-1" type="radio" name="IsBereavement" value="true" checked={postData.IsBereavement} onChange={handleRadioChange} />
                        Yes
                      </label>
                      <label className='ml-2'>
                        <input className="mr-1" type="radio" name="IsBereavement" value="false" checked={!postData.IsBereavement} onChange={handleRadioChange} />
                        No
                      </label>
                    </div>
                  </div>    
                )}  
                 {(employeeDetails.isActiveQuestionJuryDuty || employeeDetails.isActiveQuestionBereavement) && ( <div className="form-group font-italic mt-5">
                    <label>¹ In case you selected Yes Jury duty or Bereavement, please ensure that the documentation is submitted to local leaders before the pay period closes to avoid any issues with missed pay, in accordance with the handbook policy.</label>
                  </div> )}      
                         


                  <div className="d-flex justify-content-center mt-3 mb-3">
                    <button className={`notifyBtn ${isSubmited ? 'btndisabled' : ''}`} disabled={isSubmited} type="submit">{isSubmited ? 'Notifying' : 'Notify'}</button>
                  </div>
                 
                </div>
          
          )}           


        </form>
        
      )}
      </div>
       )}
    </div>
  );
};

export default FormComponent;